import PrescriptionContactFormBox from '@components/my-prescriptions/prescription-contact/PrescriptionContactFormBox';

import CloseIconBlu from '@images/svg-icons/closeIconPrescr.svg';
import EditIcon from '@images/svg-icons/editIcon.svg';
import EyeWear from '@images/svg-icons/eyewear_image.svg';
import EyeWearMobile from '@images/svg-icons/eyewearLogoMobile.svg';
import ContactLenses from '@images/svg-icons/contactLensLogoMobile.svg';
import OnlinePrescriptionMobile from '@images/svg-icons/uploadedOnlineMobileLogo.svg';
import RetailPrescriptionMobile from '@images/svg-icons/providedInStoreMobileLogo.svg';
import PrescriptionDefaultImage from '@images/svg-icons/prescriptionDefaultImage.svg';
import OnlinePrescription from '@images/svg-icons/onlinePrescription.svg';
import RetailPrescription from '@images/svg-icons/retailPrescription.svg';
import DefaultRadioButton from '@images/svg-icons/defaultRadioButton.svg';
import RadioButton from '@images/svg-icons/radioButton.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';

import {
    IAddToCartPayload,
    IDoctorAddress,
    IMySavedPrescriptionItemPrism,
    IMySavedPrescriptionItemValue,
} from '@interfaces/myAppointmentInterfaces';
import {
    addToBagService,
    downloadPrescriptionFile,
    getCLDetailsSingle,
    removePrescription,
    hidePrescription
} from '@services/myAppointmentServices';
import {
    base64IsImage,
    base64FileToImage,
    clearPrescriptionFileName,
    getFormattedDate,
    getLabelFallback,
    isToday,
    isYesterday,
} from '@utils/utils';
import moment from 'moment';
import { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'src/store/reducers';
import { Checkbox } from '@mui/material';
import Modal from '@components/styled-UI/Modal';

type PrescriptionPreviewProps = {
    firstName: string;
    lastName: string;
    lastUpdate: string;
    lastCreate: string;
    expiration?: string;
    prescriptionName: string;
    prescriptionValue: IMySavedPrescriptionItemValue;
    prism: IMySavedPrescriptionItemPrism;
    prescriptionId: string;
    isContactLenses: boolean;
    doctor?: string;
    clinic?: string;
    doctorAddress?: string;
    fileName: string;
    telephone?: string;
    leftCatentryId?: number;
    rightCatentryId?: number;
    leftCatentryParentId?: number;
    rightCatentryParentId?: number;
    prescriptionOriginMode: string;
    sameParent?: string;
    brandCLLeft?: string;
    brandCLRight?: string;
    rightPicture?: string;
    leftPicture?: string;
    nameCLRight?: string;
    nameCLLeft?: string;
    partnumber?: string;
    isPrescriptionDefault?: boolean;
    setSelectedPrescriptionAsDefault?: (prescriptionId: string, isContactLensesSelected: boolean) => void;
    deleteDefaultPrescription?: (prescriptionId: string, isContactLensesSelected: boolean) => void;
    buyableCLRight?: boolean;
    buyableCLLeft?: boolean;
    isPublishedCLLeft?: boolean;
    isPublishedCLRight?: boolean;
};

const PrescriptionPreview = (props: PrescriptionPreviewProps): ReactElement => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const config = useSelector((state: RootState) => state.common.config);
    const isMobile = useSelector((state: RootState) => state.common.isMobile);
    const justUpdateId = useSelector((state: RootState) => state.myAppointment.justUpdateId);
    const addToCartRequestStatus = useSelector(
        (state: RootState) => state.myAppointment.addToCartRequestStatus
    );
    const isRetail: boolean = props?.prescriptionOriginMode.includes('retail');

    const timezoneOffset = new Date().getTimezoneOffset();

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const oneHourAgo = new Date(new Date().setMinutes(new Date().getMinutes() + timezoneOffset));
    oneHourAgo.setHours(oneHourAgo.getHours() - 1);

    const placeHolder = '-';

    const [isPrescriptionAlertVisible, setIsPrescriptionAlertVisible] = useState<boolean>(
        moment(!!props.lastUpdate ? props.lastUpdate : props.lastCreate).toDate() < oneYearAgo ||
        (!!props.expiration && moment(props.expiration).toDate() < new Date())
    );

    const [isPrescriptionExpired, setIsPrescriptionExpired] = useState<boolean>(
        !!props.expiration && moment(props.expiration).toDate() < new Date()
    );

    const [isPrescriptionDetailsModalOpen, setIsPrescriptionDetailsModalOpen] =
        useState<boolean>(false);
    const [isPrescriptionDetailsModalCLOpen, setIsPrescriptionDetailsModalCLOpen] =
        useState<boolean>(false);
    const [isPrescriptionDeleteModalOpen, setIsPrescriptionDeleteModalOpen] =
        useState<boolean>(false);
    const [rightCL, setRightCL] = useState(null);
    const [leftCL, setLeftCL] = useState(null);
    const [prescriptionFile, setPrescriptionFile] = useState(null);
    const [prescriptionImage, setPrescriptionImage] = useState(null);
    const [annualSupplyQty, setAnnualSupplyQty] = useState(null);
    const [annualSupplyDiscount, setAnnualSupplyDiscount] = useState(null);
    const [sixMonthQty, setSixMonthQty] = useState(null);
    const [sixMonthDiscount, setSixMonthDiscount] = useState(null);
    const [powerOD, setPowerOD] = useState(null);
    const [powerOS, setPowerOS] = useState(null);
    const [bcOD, setBcOD] = useState(null);
    const [bcOS, setBcOS] = useState(null);
    const [diaOD, setDiaOD] = useState(null);
    const [diaOS, setDiaOS] = useState(null);
    const [cylOD, setCylOD] = useState(null);
    const [cylOS, setCylOS] = useState(null);
    const [colorOD, setColorOD] = useState(null);
    const [colorOS, setColorOS] = useState(null);
    const [addOD, setAddOD] = useState(null);
    const [addOS, setAddOS] = useState(null);
    const [axisOD, setAxisOD] = useState(null);
    const [axisOS, setAxisOS] = useState(null);
    const [dominanceOD, setDominanceOD] = useState(null);
    const [dominanceOS, setDominanceOS] = useState(null);
    const [boxOD, setBoxOD] = useState(null);
    const [boxOS, setBoxOS] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isAddingToBagCL, setIsAddingToBagCL] = useState<boolean>(false);

    const prescriptionFileSize = !!prescriptionFile
        ? ((4 * Math.ceil(prescriptionFile?.length / 3) * 0.5624896334383812) / 1000000).toFixed(2)
        : '';

    const clearFileName = !!props.fileName
        ? clearPrescriptionFileName(props.fileName, props.prescriptionId)
        : '';


    const onCloseAlertClick = () => {
        setIsPrescriptionAlertVisible(false);
    };

    const navigateEditPrescription = () => {
        document.body.style.overflow = ''

        navigate(
            (!props.isContactLenses
                ? config.pagesUrl.myAppointments.editPrescriptionForm
                : config.pagesUrl.myAppointments.editPrescriptionContact
            ).replace(':id', props.prescriptionId)
        );
    };

    const eyeExamLandingUrl =
        config.callFromComponentToTO.myAppointments.eyeExams.examLanding ??
        config.callFromComponentToTO.myAppointments.eyeExams.scheduleEyeExam;

    const createSupply = partNumber => {
        // FIXME: v.fortunato - this function does nothing as it tries to retrieve discounted prices using ItemBean partNumber instead of ProductBean
        // Moreover since R2.2026 discountedPricesManager is deprecated
        if (typeof discountedPricesManager !== 'undefined' && discountedPricesManager) {
            // Annual supply
            var _discountedPriceResult = discountedPricesManager.findLastUnitPrice(partNumber);

            if (_discountedPriceResult !== undefined && _discountedPriceResult !== null) {
                setAnnualSupplyQty(_discountedPriceResult[0]);
                setAnnualSupplyDiscount(
                    parseFloat(
                        (
                            (Math.round(parseFloat(_discountedPriceResult[2].toString()) * 100) /
                                100) *
                            -1
                        ).toString()
                    ).toFixed(2)
                );
            }

            // 6 months supply
            var _resultIsTheSame = false;
            var _discountedPriceResultMonths =
                discountedPricesManager.findFirstUnitPrice(partNumber);

            if (
                _discountedPriceResultMonths !== undefined &&
                _discountedPriceResultMonths !== null
            ) {
                if (_discountedPriceResult !== undefined && _discountedPriceResult !== null) {
                    if (
                        _discountedPriceResultMonths[0] == _discountedPriceResult[0] &&
                        _discountedPriceResultMonths[2] == _discountedPriceResult[2]
                    ) {
                        _resultIsTheSame = true;
                    }
                }

                if (!_resultIsTheSame) {
                    // If annual and 6 months are the same we don't show
                    setSixMonthQty(_discountedPriceResultMonths[0]);
                    setSixMonthDiscount(
                        parseFloat(
                            (
                                (Math.round(
                                    parseFloat(_discountedPriceResultMonths[2].toString()) * 100
                                ) /
                                    100) *
                                -1
                            ).toString()
                        ).toFixed(2)
                    );
                }
            }
        }
    };

    useEffect(() => {
        if (isPrescriptionDetailsModalCLOpen && !prescriptionFile && !!props.fileName) {
            downloadPrescriptionFile(config.mockRequest, config.storeId, props.fileName).then(
                response => {
                    if (!!response?.prescriptionFile) {
                        setPrescriptionFile(response?.prescriptionFile);

                        if (base64IsImage(response.prescriptionFile)) {
                            setPrescriptionImage(base64FileToImage(response.prescriptionFile));
                        }
                    }
                }
            );
        }
    }, [isPrescriptionDetailsModalCLOpen]);

    useEffect(() => {
        /**
         * Fetch the CLs details saved in the prescription
         */

        const sameCatentryId: boolean = props?.rightCatentryId === props?.leftCatentryId;

        if (isRetail !== true) {
            if (!!props.rightCatentryId) {
                getCLDetailsSingle(
                    config.mockRequest,
                    config.storeId,
                    config.catalogId,
                    props.rightCatentryId.toString()
                )
                    .then((res: any) => {
                        if (!!res) {
                            setRightCL(res);

                            if (sameCatentryId) {
                                setLeftCL(res);

                                var partNumber = res.partNumber.toLowerCase();

                                createSupply(partNumber);
                            }
                        } else {
                            console.error('>>> Error on retrieve right CL details');
                        }
                    })
                    .catch(error => {
                        console.error(
                            '>>> Error on retrieve right CL details',
                            error.status,
                            ', error: ',
                            error
                        );
                    });
            }

            if (!!props.leftCatentryId && !sameCatentryId) {
                getCLDetailsSingle(
                    config.mockRequest,
                    config.storeId,
                    config.catalogId,
                    props.leftCatentryId.toString()
                )
                    .then((res: any) => {
                        if (!!res) {
                            setLeftCL(res);
                        } else {
                            console.error('>>> Error on retrieve left CL details');
                        }
                    })
                    .catch(error => {
                        console.error(
                            '>>> Error on retrieve left CL details',
                            error.status,
                            ', error: ',
                            error
                        );
                    });
            }
        } else {
            if (!!props.rightCatentryId && !!props.isPublishedCLRight) {
                getCLDetailsSingle(
                    config.mockRequest,
                    config.storeId,
                    config.catalogId,
                    props.rightCatentryId.toString()
                )
                    .then((res: any) => {
                        if (!!res) {
                            setRightCL(res);
                        } else {
                            console.error('>>> Error on retrieve right CL details');
                        }
                    })
                    .catch(error => {
                        console.error(
                            '>>> Error on retrieve right CL details',
                            error.status,
                            ', error: ',
                            error
                        );
                    });
            } else {
                const resDx = {
                    brand: props.brandCLRight,
                    id: props.rightCatentryId,
                    image: props.rightPicture,
                    name: props.nameCLRight,
                    parentId: props.rightCatentryParentId,
                    partNumber: null,
                    price: null,
                    attributes: {
                        add: props.prescriptionValue.rightAdd !== null ? [props.prescriptionValue.rightAdd] : undefined,
                        axis: props.prescriptionValue.rightAxis !== null ? [props.prescriptionValue.rightAxis] : undefined,
                        baseCurve: props.prescriptionValue.rightBC !== null ? [props.prescriptionValue.rightBC] : undefined,
                        color: ['Clear'],
                        cylinder: props.prescriptionValue.rightCyl !== null ? [props.prescriptionValue.rightCyl] : undefined,
                        diameter: props.prescriptionValue.rightDia !== null ? [props.prescriptionValue.rightDia] : undefined,
                        power: props.prescriptionValue.rightSphere !== null ? [props.prescriptionValue.rightSphere] : undefined,
                        dominance: props.prescriptionValue.rightDominance !== null ? [props.prescriptionValue.rightDominance] : undefined

                    }
                }

                setRightCL(resDx);
            }

            if (!!props.leftCatentryId && !!props.isPublishedCLLeft) {
                getCLDetailsSingle(
                    config.mockRequest,
                    config.storeId,
                    config.catalogId,
                    props.leftCatentryId.toString()
                )
                    .then((res: any) => {
                        if (!!res) {
                            setLeftCL(res);
                        } else {
                            console.error('>>> Error on retrieve right CL details');
                        }
                    })
                    .catch(error => {
                        console.error(
                            '>>> Error on retrieve right CL details',
                            error.status,
                            ', error: ',
                            error
                        );
                    });
            } else {
                const resSx = {
                    brand: props.brandCLLeft,
                    id: props.leftCatentryId,
                    image: props.leftPicture,
                    name: props.nameCLLeft,
                    parentId: props.leftCatentryParentId,
                    partNumber: null,
                    price: null,
                    attributes: {
                        add: props.prescriptionValue.leftAdd !== null ? [props.prescriptionValue.leftAdd] : undefined,
                        axis: props.prescriptionValue.leftAxis !== null ? [props.prescriptionValue.leftAxis] : undefined,
                        baseCurve: props.prescriptionValue.leftBC !== null ? [props.prescriptionValue.leftBC] : undefined,
                        color: ['Clear'],
                        cylinder: props.prescriptionValue.leftCyl !== null ? [props.prescriptionValue.leftCyl] : undefined,
                        diameter: props.prescriptionValue.leftDia !== null ? [props.prescriptionValue.leftDia] : undefined,
                        power: props.prescriptionValue.leftSphere !== null ? [props.prescriptionValue.leftSphere] : undefined,
                        dominance: props.prescriptionValue.leftDominance !== null ? [props.prescriptionValue.leftDominance] : undefined
                    }
                }

                setLeftCL(resSx);
            }

            if (!!props.leftCatentryId && !!props.rightCatentryId && !!props.partnumber && (props.leftCatentryId === props.rightCatentryId)) {
                var partNumber = props.partnumber.toLowerCase();

                createSupply(partNumber);
            }
        }
    }, [props.rightCatentryId, props.leftCatentryId]);

    const addToBag = () => {
        if (isAddingToBagCL) {
            return;
        }

        let setErrorForm = {};

        if ((!!boxOD === false || boxOD === '') && !!rightCL) {
            setErrorForm = {
                ...setErrorForm,
                right_eye_boxes: 'error',
            };
        }

        if ((!!boxOS === false || boxOS === '') && !!leftCL) {
            setErrorForm = {
                ...setErrorForm,
                left_eye_boxes: 'error',
            };
        }

        if (!!boxOD && !!boxOS && boxOD === '0' && boxOS === '0') {
            setErrorForm = {
                ...setErrorForm,
                right_eye_boxes: 'error',
                left_eye_boxes: 'error',
            };
        }

        if (Object.entries(setErrorForm).length === 0) {
            const payload: IAddToCartPayload = {
                isMockRequest: config.mockRequest,
                storeId: config.storeId,
                catalogId: config.catalogId,
                langId: config.langId.toString(),
                URL: 'GetCatalogEntryDetailsByID',
                errorViewName: 'LogonAjaxView',
                cache: false /* Must be always "false" */,
                action: 'add' /* Must be always "add" */,
                orderId: '.' /* Must be always "." */,
                partType: 'CONTACT_LENSES' /* Must be always "CONTACT_LENSES" */,
                leftLensItemId: ((props.prescriptionOriginMode.includes('retail') && props.buyableCLLeft && !!props.leftCatentryId) || (!props.prescriptionOriginMode.includes('retail') && !!props.leftCatentryId)) ? props.leftCatentryId.toString() : null /* Left eye SKU id */,
                rightLensItemId: ((props.prescriptionOriginMode.includes('retail') && props.buyableCLRight && !!props.rightCatentryId) || (!props.prescriptionOriginMode.includes('retail') && !!props.rightCatentryId)) ? props.rightCatentryId.toString() : null /* Right eye SKU id */,
                leftQuantity: boxOS /* Left eye quantity  */,
                rightQuantity: boxOD /* Right eye quantity  */,
                leftPrescriptionId:
                    props.prescriptionId.toString() /* Left account prescription id */,
                rightPrescriptionId:
                    props.prescriptionId.toString() /* Right account prescription id */,
                autosetquantity: 'supplyAmount',
                isPrescriptionRetail: props.prescriptionOriginMode.includes('retail'),
                catEntryId: !!props.rightCatentryParentId ? props.rightCatentryParentId.toString() : !!props.leftCatentryParentId ? props.leftCatentryParentId.toString() : ''
            };

            setIsAddingToBagCL(true);

            dispatch(addToBagService(config.storeId, config.catalogId, config.langId, payload));
        }

        setFormErrors(setErrorForm);
    };

    useEffect(() => {
        if (addToCartRequestStatus !== 'LOADING') {
            setIsAddingToBagCL(false);
        }
    }, [addToCartRequestStatus]);

    //DEFAULT PRESCRIPTION LOGIC
    const handleSetDefaultPrescriptionMethod = () => {
        props.setSelectedPrescriptionAsDefault(props.prescriptionId.toString(), props.isContactLenses);
    }

    const handleOpenModalCl = () => {
        setIsPrescriptionDetailsModalCLOpen(true);
        document.body.style.overflow = 'hidden'
    }

    const handleCloseModalCl = () => {
        setIsPrescriptionDetailsModalCLOpen(false);
        document.body.style.overflow = ''
    }

    const handleOpenModal = () => {
        setIsPrescriptionDetailsModalOpen(true);
        document.body.style.overflow = 'hidden'
    }

    const handleCloseModal = () => {
        setIsPrescriptionDetailsModalOpen(false);
        document.body.style.overflow = ''
    }

    const handleOpenDeleteModal = () => {
        setIsPrescriptionDeleteModalOpen(true);
        document.body.style.overflow = 'hidden'
    }

    const handleCloseDeleteModal = () => {
        setIsPrescriptionDeleteModalOpen(false);
        document.body.style.overflow = ''
    }

    return (
        <div
            className={
                'prescription-preview-container' + (isPrescriptionAlertVisible ? ' one-year' : '')
            }
            role="region"
            aria-label={`prescription preview for ${props.prescriptionName}`}
        >
            {/* Eyewear logo and span and online or retail specification (desktop) */}
            <div className="header-buttons">
                <div className="header-buttons-container">
                    <div className="eyewear-and-online-or-store-container">
                        <div className="eyewear-prescription">
                            <span className="eyewear-icon">
                                {props.isContactLenses ? <ContactLenses /> : <EyeWear />}
                            </span>
                            <span className="eyewear-label">
                                {props.isContactLenses
                                    ? getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_CL_PRESCRIPTION')
                                    : getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_EYEWEAR_PRESCRIPTION'
                                    )}
                            </span>

                            {/* Vertical line */}
                            <div className="vertical-separator" />

                            {/* Online or Retail icon */}
                            <span className='eyewear-icon'>
                                {props.prescriptionOriginMode.includes('retail') ? <RetailPrescription /> : <OnlinePrescription />}
                            </span>

                            {/* Online or Retail label */}
                            <span className='eyewear-label'>
                                {props.prescriptionOriginMode.includes('retail')
                                    ? getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_IN_STORE')
                                    : getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_ONLINE')
                                }
                            </span>
                        </div>
                    </div>

                    {/* Delete and Edit buttons (desktop) */}
                    <div className="edit-delete-buttons-container-desk">
                        <div className="delete-button-container">
                            <button
                                className="primary-button-negative button-size-small"
                                onClick={() => {
                                    handleOpenDeleteModal();

                                    try {
                                        window?.tealium_data2track.push({
                                            id: 'Impression',
                                            Page_Section2: 'DeleteRX',
                                        });
                                    } catch (e) {
                                        console.error(e);
                                    }
                                }}
                                type="submit"
                            >
                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_TILE_DELETE')}
                            </button>
                        </div>

                        {!props.prescriptionOriginMode.includes('retail') && ((!!props.leftCatentryId && !!props.rightCatentryId) || !!props.isContactLenses === false) && (
                            <div className="edit-button-container">
                                <button
                                    className="primary-button-negative button-size-small"
                                    onClick={navigateEditPrescription}
                                    type="submit"
                                >
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_TILE_EDIT')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {(!!(props.lastUpdate && props.lastCreate) || !!props.expiration) && (
                    <>
                        {isPrescriptionExpired ? (
                            <div className='expiration-container'>
                                <span className='expires-on-span'>
                                    <span className="expires-on-span-title">
                                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_TILE_EXPIRED')}
                                    </span>
                                    <span className="expires-on-span-date">
                                        {getFormattedDate(props.expiration)}
                                    </span>
                                </span>
                            </div>
                        ) : (
                            <div className="updated-on-container">
                                <span
                                    className={
                                        'updated-on-span' +
                                        (moment(props.lastUpdate).toDate() > oneHourAgo
                                            ? ' hide'
                                            : '')
                                    }
                                >
                                    <span className='update-on-span-title'>
                                        {props.lastUpdate !== props.lastCreate
                                            ? getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_TILE_UPDATE'
                                            )
                                            : getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_TILE_UPLOAD'
                                            )
                                        }
                                    </span>

                                    <span className='update-on-span-date'>
                                        {props.lastUpdate !== props.lastCreate
                                            ? (isToday(props.lastUpdate)
                                                ? getLabelFallback(
                                                    'MYPROFILE_MY_PRESCRIPTIONS_TILE_TODAY'
                                                )
                                                : isYesterday(props.lastUpdate)
                                                    ? getLabelFallback(
                                                        'MYPROFILE_MY_PRESCRIPTIONS_TILE_YESTERDAY'
                                                    )
                                                    : getFormattedDate(props.lastUpdate))
                                            : ' ' +
                                            getFormattedDate(props.lastCreate)
                                        }
                                    </span>
                                </span>
                                <span
                                    className={
                                        'just-updated-span' +
                                        (moment(props.lastUpdate).toDate() > oneHourAgo
                                            ? ''
                                            : ' hide') +
                                        (!!justUpdateId &&
                                            justUpdateId.includes(props.prescriptionId + ';')
                                            ? ' green ' + justUpdateId
                                            : '')
                                    }
                                >
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_TILE_JUST_UPDATE')}
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Eyewear logo and span and updated on (mobile) */}
            <div className="headers-container-mobile">
                <div className="eyewear-and-online-or-store-container">
                    <div className="eyewear-prescription">
                        <span className="eyewear-icon">
                            {props.isContactLenses ? <ContactLenses /> : <EyeWearMobile />}
                        </span>
                        <span className="eyewear-label">
                            {props.isContactLenses
                                ? getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_CL_PRESCRIPTION_MOBILE')
                                : getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_EYEWEAR_PRESCRIPTION_MOBILE')}
                        </span>

                        {/* Vertical line */}
                        <div className="vertical-separator" />

                        {/* Online or Retail icon */}
                        <span className='eyewear-icon'>
                            {props.prescriptionOriginMode.includes('retail') ? <RetailPrescriptionMobile /> : <OnlinePrescriptionMobile />}
                        </span>

                        {/* Online or Retail label */}
                        <span className='eyewear-label'>
                            {props.prescriptionOriginMode.includes('retail')
                                ? getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_IN_STORE')
                                : getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_ONLINE')
                            }
                        </span>
                    </div>
                </div>

                {(!!(props.lastUpdate && props.lastCreate) || !!props.expiration) && (
                    <>
                        {/* Vertical line */}
                        <div className="vertical-separator" />

                        {/* Mostra solo expired e la data se scaduta, altrimenti upload/update */}
                        {isPrescriptionExpired ? (
                            <div className='expiration-container'>
                                <span className='expires-on-span'>
                                    <span className="expires-on-span-title">
                                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_TILE_EXPIRED')}
                                    </span>
                                    <span className="expires-on-span-date">
                                        {getFormattedDate(props.expiration)}
                                    </span>
                                </span>
                            </div>
                        ) : (
                            <div className="updated-on-container">
                                <span
                                    className={
                                        'updated-on-span' +
                                        (moment(props.lastUpdate).toDate() > oneHourAgo ? ' hide' : '')
                                    }
                                >
                                    <span className='update-on-span-title'>
                                        {props.lastUpdate !== props.lastCreate
                                            ? getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_TILE_UPDATE'
                                            )
                                            : getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_TILE_UPLOAD'
                                            )
                                        }
                                    </span>

                                    <span className='update-on-span-date'>
                                        {props.lastUpdate !== props.lastCreate
                                            ? (isToday(props.lastUpdate)
                                                ? getLabelFallback(
                                                    'MYPROFILE_MY_PRESCRIPTIONS_TILE_TODAY'
                                                )
                                                : isYesterday(props.lastUpdate)
                                                    ? getLabelFallback(
                                                        'MYPROFILE_MY_PRESCRIPTIONS_TILE_YESTERDAY'
                                                    )
                                                    : getFormattedDate(props.lastUpdate))
                                            : ' ' +
                                            getFormattedDate(props.lastCreate)
                                        }
                                    </span>
                                </span>
                                <span
                                    className={
                                        'just-updated-span' +
                                        (moment(props.lastUpdate).toDate() > oneHourAgo
                                            ? ''
                                            : ' hide') +
                                        (!!justUpdateId &&
                                            justUpdateId.includes(props.prescriptionId + ';')
                                            ? ' green ' + justUpdateId
                                            : '')
                                    }
                                >
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_TILE_JUST_UPDATE')}
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {isMobile && (
                <div className="full-name-and-prescription-link-container mob">
                    {/* Full name */}
                    <div className="full-name-container">{props.prescriptionName}</div>
                    <div className="updated-on-container">
                        <a
                            className="link"
                            onClick={() => {
                                props.isContactLenses
                                    ? handleOpenModalCl()
                                    : handleOpenModal();
                            }}
                        >
                            <span className="link-underline">
                                {props.isContactLenses
                                    ? getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_SHOW_PRESCRIPTION_DETAILS_BUTTON'
                                    )
                                    : getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_SHOW_PRESCRIPTION_BUTTON'
                                    )}
                            </span>
                        </a>
                    </div>
                </div>
            )}

            {/* Separator */}
            {!props.isContactLenses && (<div className="prescription-form-separator"></div>)}

            {/* Full name and Show Prescription link desk*/}
            {!isMobile && (
                <div className="full-name-and-prescription-link-container desk">
                    {/* Full name */}
                    <div className="full-name-container">{props.prescriptionName}</div>
                    <div className="updated-on-container">
                        <a
                            className="link"
                            onClick={() => {
                                props.isContactLenses
                                    ? handleOpenModalCl()
                                    : handleOpenModal();
                            }}
                        >
                            <span className="link-underline">
                                {props.isContactLenses
                                    ? getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_SHOW_PRESCRIPTION_DETAILS_BUTTON'
                                    )
                                    : getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_SHOW_PRESCRIPTION_BUTTON'
                                    )}
                            </span>
                        </a>
                    </div>
                </div>
            )}

            {/* Older than 1 year */}
            {isPrescriptionAlertVisible && (
                <div className="one-year-container">
                    <div className="labels-container">
                        <span className="one-year-label_1">
                            {isPrescriptionExpired
                                ? getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_EXPIRES_1')
                                : getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_OLDER_ONE_YEAR_1')}
                        </span>
                        <span className="one-year-label_2">
                            <a className="link-exam" href={eyeExamLandingUrl}>
                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_BOOK_EYE_EXAM')}
                            </a>
                            {isPrescriptionExpired
                                ? getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_EXPIRES_2')
                                : getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_OLDER_ONE_YEAR_2')}
                        </span>
                    </div>

                    <div className="x-icon-alert-container">
                        <button
                            role="region"
                            aria-label="back button"
                            className="back-button"
                            onClick={() => onCloseAlertClick()}
                        >
                            <span className="start-icon">
                                <CloseIconBlu />
                            </span>
                        </button>
                    </div>
                </div>
            )}

            {/* Delete and Edit buttons (mobile) */}
            <div className="edit-delete-buttons-container-mobile">
                {!props.prescriptionOriginMode.includes('retail') && (
                    <div className="edit-button-container">
                        <button
                            className="primary-button-negative button-size-small"
                            onClick={navigateEditPrescription}
                            type="submit"
                        >
                            {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_TILE_EDIT')}
                        </button>
                    </div>
                )}


                <div className="delete-button-container">
                    <button
                        className="primary-button-negative button-size-small"
                        onClick={() => handleOpenDeleteModal()}
                    >
                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_TILE_DELETE')}
                    </button>
                </div>
            </div>

            {props.isContactLenses && (
                <>
                    {/* Horizontal line */}
                    <div className="prescription-form-separator" />
                    <div className="container-cl">
                        <div className="container-cl-row">
                            {/* Add more row for different right and left */}
                            <PrescriptionContactFormBox
                                isNotEditable={true}
                                doubleProduct={
                                    props.leftCatentryParentId !== props.rightCatentryParentId || props.sameParent === "false"
                                }
                                rightCL={rightCL}
                                leftCL={leftCL}
                                powerOD={powerOD}
                                setPowerOD={setPowerOD}
                                powerOS={powerOS}
                                setPowerOS={setPowerOS}
                                bcOD={bcOD}
                                setBcOD={setBcOD}
                                bcOS={bcOS}
                                setBcOS={setBcOS}
                                diaOD={diaOD}
                                setDiaOD={setDiaOD}
                                diaOS={diaOS}
                                setDiaOS={setDiaOS}
                                cylOD={cylOD}
                                setCylOD={setCylOD}
                                cylOS={cylOS}
                                setCylOS={setCylOS}
                                axisOD={axisOD}
                                setAxisOD={setAxisOD}
                                axisOS={axisOS}
                                setAxisOS={setAxisOS}
                                boxOD={boxOD}
                                setBoxOD={setBoxOD}
                                boxOS={boxOS}
                                setBoxOS={setBoxOS}
                                colorOD={colorOD}
                                setColorOD={setColorOD}
                                colorOS={colorOS}
                                setColorOS={setColorOS}
                                addOD={addOD}
                                setAddOD={setAddOD}
                                addOS={addOS}
                                setAddOS={setAddOS}
                                dominanceOD={dominanceOD}
                                setDominanceOD={setDominanceOD}
                                dominanceOS={dominanceOS}
                                setDominanceOS={setDominanceOS}
                                formErrors={formErrors}
                                annualSupplyDiscount={annualSupplyDiscount}
                                annualSupplyQty={annualSupplyQty}
                                sixMonthDiscount={sixMonthDiscount}
                                sixMonthQty={sixMonthQty}
                                isBuyableLeft={props.buyableCLLeft}
                                isBuyableRight={props.buyableCLRight}
                                isPrescriptionRetail={props.prescriptionOriginMode.includes('retail')}
                            />
                        </div>
                        {(!!formErrors['right_eye_boxes'] || !!formErrors['left_eye_boxes']) && (
                            <div className="quantity-error">
                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_QUANTITY_ERROR')}
                            </div>
                        )}
                        <div className="container-cl-supply">
                            {!!annualSupplyDiscount && !!annualSupplyQty && (
                                <div className="container-cl-supply-left">
                                    <span className="bold">
                                        {
                                            getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_ANNUAL_SUPPLY_1'
                                            ).replace(
                                                '##NUMVALUE##',
                                                annualSupplyDiscount
                                            ) /* TODO CORRECT VALUE */
                                        }
                                    </span>{' '}
                                    {getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_ANNUAL_SUPPLY_2'
                                    ).replace('###NUMBOX###', annualSupplyQty)}
                                </div>
                            )}
                            {!!sixMonthDiscount && !!sixMonthQty && (
                                <div className="container-cl-supply-right">
                                    <span className="bold">
                                        {
                                            getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_SIX_MONTH_SUPPLY_1'
                                            ).replace(
                                                '##NUMVALUE##',
                                                sixMonthDiscount
                                            ) /* TODO CORRECT VALUE */
                                        }{' '}
                                    </span>{' '}
                                    {getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_SIX_MONTH_SUPPLY_2'
                                    ).replace('###NUMBOX###', sixMonthQty)}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {props.isContactLenses &&
                props.prescriptionOriginMode.includes('retail') &&
                isPrescriptionExpired && (
                    <div className="container-cl-button">
                        <button
                            className="primary-button"
                            onClick={() => window.open(eyeExamLandingUrl, '_parent')}
                        >
                            {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_BOOK_EYE_EXAM')}
                        </button>
                    </div>
                )
            }

            {props.isContactLenses &&
                !isPrescriptionExpired &&
                ((props.prescriptionOriginMode.includes('retail') && (props.buyableCLLeft || props.buyableCLRight)) ||
                    !props.prescriptionOriginMode.includes('retail')) && (
                    <div className="container-cl-button">
                        <button className="primary-button" onClick={addToBag}>
                            {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_CL_BUTTON')}
                        </button>
                    </div>
                )
            }

            <Modal
                customStyle={isMobile ? 'delete-prescription-sm' : 'delete-prescription-desk'}
                isOpen={isPrescriptionDeleteModalOpen}
                close={() => handleCloseDeleteModal()}
                isCentered
            >
                <div className="delete-prescription-modal">
                    <div role="region" className="title">
                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_DELETE_MODAL_TITLE')}
                    </div>
                    <div className="buttons-container">
                        <button
                            role="region"
                            aria-label="My payment methods delete modal remove button"
                            data-element-id="MyPrescription_DeleteOverlay-Confirm"
                            data-description="Remove"
                            className="primary-button button-size-medium"
                            onClick={() => {
                                if (isRetail) {
                                    dispatch(
                                        hidePrescription(
                                            config.mockRequest,
                                            config.storeId,
                                            props.prescriptionId
                                        )
                                    )
                                } else {
                                    dispatch(
                                        removePrescription(
                                            config.mockRequest,
                                            config.storeId,
                                            props.prescriptionId
                                        )
                                    );
                                }

                                if (props.isPrescriptionDefault) {
                                    props.deleteDefaultPrescription(props.prescriptionId.toString(), props.isContactLenses);
                                }

                                handleCloseDeleteModal();

                                try {
                                    window?.tealium_data2track.push({
                                        id: 'Click',
                                        data_element_id: 'MyPrescription_DeleteOverlay-Confirm',
                                    });
                                } catch (e) {
                                    console.error(e);
                                }
                            }}
                        >
                            {getLabelFallback(
                                'MYPROFILE_MY_PRESCRIPTIONS_DELETE_MODAL_REMOVE_CTA_LABEL'
                            )}
                        </button>
                        <button
                            role="region"
                            data-description="Back"
                            className="primary-button-negative button-size-medium"
                            data-element-id="MyPrescription_DeleteOverlay-Cancel"
                            onClick={() => handleCloseDeleteModal()}
                            data-analytics_available_call="0"
                        >
                            {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_CLOSE_MODAL_REMOVE_CTA_LABEL')}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Prescription preview modal */}
            <div className="modal-prescription-preview-cl">
                <Modal
                    isOpen={isPrescriptionDetailsModalCLOpen}
                    close={() => handleCloseModalCl()}
                    isCentered
                    showCloseIcon={true}
                    isPrescriptionInfoOpen={true}
                >
                    <div className="props-container-prescription-preview">
                        <div className="modal-content-container">
                            {/* TITLE */}
                            <div className="title-container">
                                <span className="title-your-prescription">Your prescription details</span>
                            </div>
                            {/* SUBTITLE */}
                            <div className="subtitle-container">
                                <span className="subtitle-span">
                                    {getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_SUBTITLE_PREVIEW_CL'
                                    )}
                                </span>
                            </div>

                            {/* DOCTOR'S DETAILS TITLE */}
                            {!isRetail && (
                                <div className="your-doctor-details-container">
                                    <span className="your-doctor-details-span">
                                        Your doctor's details
                                    </span>
                                </div>
                            )}

                            {isRetail && (
                                <div className="your-doctor-details-container">
                                    <span className="your-doctor-details-span">
                                        Your store details
                                    </span>
                                </div>
                            )}

                            {/* DOCTOR'S DETAILS TABLE */}
                            {!isRetail && (
                                <div className="table-container">
                                    {!isMobile && (<>
                                        {/* TABLE HEADERS */}
                                        <div className="headers-container">
                                            <div className="header-container">
                                                <div className="header-span-container">DOCTOR NAME</div>
                                            </div>
                                            <div className="header-container">
                                                <div className="header-span-container">CLINIC NAME</div>
                                            </div>
                                            <div className="header-container address-106px">
                                                <div className="header-span-container address">ADDRESS</div>
                                            </div>
                                        </div>
                                        {/* TABLE VALUES */}
                                        <div className="values-container">
                                            <div className="value-container">
                                                <div className="value-span-container">
                                                    <span className="value-span">{props.doctor}</span>
                                                </div>
                                            </div>
                                            <div className="value-container">
                                                <div className="value-span-container">
                                                    <span className="value-span">{props.clinic}</span>
                                                </div>
                                            </div>
                                            <div className="value-container address-106px">
                                                <div className="doctor-address-container">
                                                    {props.doctorAddress && (
                                                        <div className="address-street-and-city-value-container">
                                                            <div className="address-street-value">
                                                                <div className="address-street">
                                                                    {!!props.doctorAddress && props.doctorAddress.split(',')[0]},{' '}
                                                                    {!!props.doctorAddress && props.doctorAddress.split(',')[1].split(' ')[1]}{' '}
                                                                </div>
                                                            </div>
                                                            <div className="address-city-value">
                                                                <div className="address-city">
                                                                    {!!props.doctorAddress && props.doctorAddress.split(',')[1].split(' ')[2]},{' US'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {props.telephone && (
                                                        <div className="doctor-address">
                                                            {props.telephone}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                    )}
                                    {isMobile && (<>
                                        <div className='header-and-value-container'>
                                            <div className="headers-container">
                                                <div className="header-container">
                                                    <div className="header-span-container">DOCTOR NAME</div>
                                                </div>
                                            </div>
                                            <div className="values-container">
                                                <div className="value-container">
                                                    <div className="value-span-container">
                                                        <span className="value-span">{props.doctor}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='header-and-value-container'>
                                            <div className="headers-container">
                                                <div className="header-container">
                                                    <div className="header-span-container">CLINIC NAME</div>
                                                </div>
                                            </div>
                                            <div className="values-container">
                                                <div className="value-container">
                                                    <div className="value-span-container">
                                                        <span className="value-span">{props.clinic}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='header-and-value-container'>
                                            <div className="headers-container">
                                                <div className="header-container address-106px">
                                                    <div className="header-span-container address">ADDRESS</div>
                                                </div>
                                            </div>
                                            <div className="values-container">
                                                <div className="value-container address-106px">
                                                    <div className="doctor-address-container">
                                                        {props.doctorAddress && (
                                                            <div className="address-street-and-city-value-container">
                                                                <div className="address-street-value">
                                                                    <div className="address-street">
                                                                        {!!props.doctorAddress && props.doctorAddress.split(',')[0]},{' '}
                                                                        {!!props.doctorAddress && props.doctorAddress.split(',')[1].split(' ')[1]}{' '}
                                                                    </div>
                                                                </div>
                                                                <div className="address-city-value">
                                                                    <div className="address-city">
                                                                        {!!props.doctorAddress && props.doctorAddress.split(',')[1].split(' ')[2]},{' US'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {props.telephone && (
                                                            <div className="doctor-address">
                                                                {props.telephone}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                    )}
                                </div>
                            )}

                            {isRetail && (
                                <div className="table-container">
                                    {!isMobile && (<>
                                        {/* TABLE HEADERS */}
                                        <div className="headers-container">
                                            <div className="header-container">
                                                <div className="header-span-container">STORE NAME</div>
                                            </div>
                                            <div className="header-container address-106px">
                                                <div className="header-span-container address">ADDRESS</div>
                                            </div>
                                        </div>
                                        {/* TABLE VALUES */}
                                        <div className="values-container">
                                            <div className="value-container">
                                                <div className="value-span-container">
                                                    <span className="value-span">{props.doctor.trim().charAt(0).toUpperCase() + props.doctor.trim().slice(1)}</span>
                                                </div>
                                            </div>
                                            <div className="value-container address-106px">
                                                <div className="doctor-address-container">
                                                    {props.doctorAddress && (
                                                        <div className="address-street-and-city-value-container">
                                                            <div className="address-street-value">
                                                                <div className="address-street">
                                                                    <span>{!!props.doctorAddress && props.doctorAddress.split(',')[0]}</span>
                                                                </div>
                                                            </div>
                                                            <div className="address-city-value">
                                                                <div className="address-city">
                                                                    <span>{!!props.doctorAddress && props.doctorAddress.split(',')[1].trim()}</span>{', '}{props.doctorAddress.split(',')[2].trim()}
                                                                </div>
                                                            </div>
                                                            <div className="address-zip-value">
                                                                <div className="address-zip">
                                                                    {!!props.doctorAddress && props.doctorAddress.split(',')[3].trim()},{' US'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {props.telephone && (
                                                        <div className="doctor-address retail-phone">
                                                            {'+1 '}{props.telephone}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                    )}
                                    {isMobile && (
                                        <>
                                            <div className='header-and-value-container'>
                                                <div className="headers-container">
                                                    <div className="header-container">
                                                        <div className="header-span-container">STORE NAME</div>
                                                    </div>
                                                </div>
                                                <div className="values-container">
                                                    <div className="value-container">
                                                        <div className="value-span-container">
                                                            <span className="value-span">{props.doctor.trim().charAt(0).toUpperCase() + props.doctor.trim().slice(1)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='header-and-value-container'>
                                                <div className="headers-container">
                                                    <div className="header-container address-106px">
                                                        <div className="header-span-container address">ADDRESS</div>
                                                    </div>
                                                </div>
                                                <div className="values-container">
                                                    <div className="value-container address-106px">
                                                        <div className="doctor-address-container">
                                                            {props.doctorAddress && (
                                                                <div className="address-street-and-city-value-container">
                                                                    <div className="address-street-value">
                                                                        <div className="address-street">
                                                                            <span>{!!props.doctorAddress && props.doctorAddress.split(',')[0]}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="address-city-value">
                                                                        <div className="address-city">
                                                                            <span>{!!props.doctorAddress && props.doctorAddress.split(',')[1].trim()}</span>{', '}{props.doctorAddress.split(',')[2].trim()}
                                                                        </div>
                                                                    </div>
                                                                    <div className="address-zip-value">
                                                                        <div className="address-zip">
                                                                            {!!props.doctorAddress && props.doctorAddress.split(',')[3].trim()},{' US'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {props.telephone && (
                                                                <div className="doctor-address retail-phone">
                                                                    {'+1 '}{props.telephone}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* YOUR SUBMISSION METHOD */}
                            {!isRetail && props.prescriptionOriginMode != 'callDoctor' && (
                                <div className="your-submission-method-container">
                                    <div className="your-submission-method-title">
                                        <span className="your-submission-method-title-span">
                                            Your submission method
                                        </span>
                                    </div>
                                    <div className="file-container">
                                        <div className="file-image-container">
                                            {!!prescriptionImage ? (
                                                <img
                                                    className="prescription-preview-image"
                                                    src={prescriptionImage}
                                                />
                                            ) : (
                                                <PrescriptionDefaultImage />
                                            )}
                                        </div>

                                        {!!props.fileName && (
                                            <div className="file-text-container">
                                                {clearFileName} - [{prescriptionFileSize} MB]
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            </div>

            <div className="modal-prescription-preview">
                <Modal
                    isOpen={isPrescriptionDetailsModalOpen}
                    close={() => handleCloseModal()}
                    isCentered
                    showCloseIcon={true}
                >
                    <div className="modal-container-prescription-preview">
                        <div className="modal-content-container">
                            <div className="title-and-close-button-container">
                                <span className="title-your-prescription">{props.prescriptionName}</span>
                                {!isRetail && (
                                    <div className="edit-prescription-container">
                                        <div className="editIcon">
                                            <EditIcon
                                                onClick={() => {
                                                    navigateEditPrescription();
                                                }}
                                                data-analytics_available_call="0"
                                            />
                                        </div>
                                        <a
                                            className="edit-prescription-link"
                                            data-analytics_available_call="0"
                                            onClick={navigateEditPrescription}
                                        >
                                            <span className="link-underline">Edit prescription</span>
                                        </a>
                                    </div>
                                )}
                            </div>

                            <div className="first-table-container">
                                <div className="eyes-headers-container mobile-headers">
                                    <div className="eyes-header-empty box-container"></div>
                                    <div className="header-od box-container">
                                        <span className="header-span">
                                            {getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_OD_MODAL'
                                            )}
                                        </span>
                                    </div>
                                    <div className="header-os box-container">
                                        <span className="header-span">
                                            {getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_OS_MODAL'
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className="sph-parameters-container mobile-headers">
                                    <div className="cyl-header box-container">
                                        <span className="header-span">
                                            {getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_SPH_MODAL'
                                            )}
                                        </span>
                                    </div>
                                    <div className="cyl-od box-container">
                                        {!!props.prescriptionValue.rightSphere
                                            ? props.prescriptionValue.rightSphere
                                            : placeHolder}
                                    </div>
                                    <div className="cyl-os box-container">
                                        {!!props.prescriptionValue.leftSphere
                                            ? props.prescriptionValue.leftSphere
                                            : placeHolder}
                                    </div>
                                </div>
                                <div className="cyl-parameters-container mobile-headers">
                                    <div className="cyl-header box-container">
                                        <span className="header-span">
                                            {getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_CYL_MODAL'
                                            )}
                                        </span>
                                    </div>
                                    <div className="cyl-od box-container">
                                        {!!props.prescriptionValue.rightCyl
                                            ? props.prescriptionValue.rightCyl
                                            : placeHolder}
                                    </div>
                                    <div className="cyl-os box-container">
                                        {!!props.prescriptionValue.leftCyl
                                            ? props.prescriptionValue.leftCyl
                                            : placeHolder}
                                    </div>
                                </div>
                                <div className="axis-parameters-container mobile-headers">
                                    <div className="axis-header box-container">
                                        <span className="header-span">
                                            {getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_AXIS_MODAL'
                                            )}
                                        </span>
                                    </div>
                                    <div className="axis-od box-container">
                                        {!!props.prescriptionValue.rightAxis
                                            ? props.prescriptionValue.rightAxis
                                            : placeHolder}
                                    </div>
                                    <div className="axis-os box-container">
                                        {!!props.prescriptionValue.leftAxis
                                            ? props.prescriptionValue.leftAxis
                                            : placeHolder}
                                    </div>
                                </div>
                                <div className="add-parameters-container mobile-headers">
                                    <div className="add-header box-container">
                                        <span className="header-span">
                                            {getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_ADD_MODAL'
                                            )}
                                        </span>
                                    </div>
                                    <div className="add-od box-container">
                                        {!!props.prescriptionValue.rightAdd
                                            ? props.prescriptionValue.rightAdd
                                            : placeHolder}
                                    </div>
                                    <div className="add-os box-container">
                                        {!!props.prescriptionValue.leftAdd
                                            ? props.prescriptionValue.leftAdd
                                            : placeHolder}
                                    </div>
                                </div>
                            </div>
                            {props.prism.prismEnable && (
                                <div className="second-table-container">
                                    <div className="eyes-headers-container mobile-headers">
                                        <div className="eyes-header-empty box-container mobile-headers"></div>
                                        <div className="header-od box-container">
                                            <span className="header-span">
                                                {getLabelFallback(
                                                    'MYPROFILE_MY_PRESCRIPTIONS_OD_MODAL'
                                                )}
                                            </span>
                                        </div>
                                        <div className="header-os box-container">
                                            <span className="header-span">
                                                {getLabelFallback(
                                                    'MYPROFILE_MY_PRESCRIPTIONS_OS_MODAL'
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="vp-parameters-container mobile-headers">
                                        <div className="vp-header second-box-container">
                                            <span className="header-span smaller-size">
                                                Vertical (&Delta;)
                                            </span>
                                        </div>
                                        <div className="vp-od second-box-container">
                                            {!!props.prism.odVPrism
                                                ? props.prism.odVPrism
                                                : placeHolder}
                                        </div>
                                        <div className="vp-os second-box-container">
                                            {!!props.prism.osVPrism
                                                ? props.prism.osVPrism
                                                : placeHolder}
                                        </div>
                                    </div>
                                    <div className="bd-parameters-container mobile-headers">
                                        <div className="bd-header second-box-container">
                                            <span className="header-span smaller-size">
                                                {getLabelFallback(
                                                    'ADD_PRESCRIPTION_STEP_1_BASE_DIRECTION'
                                                )}
                                            </span>
                                        </div>
                                        <div className="bd-od second-box-container">
                                            {!!props.prism.odVPrismBaseDir
                                                ? props.prism.odVPrismBaseDir
                                                : placeHolder}
                                        </div>
                                        <div className="bd-os second-box-container">
                                            {!!props.prism.osVPrismBaseDir
                                                ? props.prism.osVPrismBaseDir
                                                : placeHolder}
                                        </div>
                                    </div>
                                    <div className="hp-parameters-container mobile-headers">
                                        <div className="hp-header second-box-container">
                                            <span className="header-span smaller-size">
                                                Horizontal (&Delta;)
                                            </span>
                                        </div>
                                        <div className="hp-od second-box-container">
                                            {!!props.prism.odHPrism
                                                ? props.prism.odHPrism
                                                : placeHolder}
                                        </div>
                                        <div className="hp-os second-box-container">
                                            {!!props.prism.osHPrism
                                                ? props.prism.osHPrism
                                                : placeHolder}
                                        </div>
                                    </div>
                                    <div className="bd-parameters-container-in-out mobile-headers">
                                        <div className="bd-header second-box-container">
                                            <span className="header-span smaller-size">
                                                {getLabelFallback(
                                                    'ADD_PRESCRIPTION_STEP_1_BASE_DIRECTION'
                                                )}
                                            </span>
                                        </div>
                                        <div className="bd-od second-box-container">
                                            {!!props.prism.odHPrismBaseDir
                                                ? props.prism.odHPrismBaseDir
                                                : placeHolder}
                                        </div>
                                        <div className="bd-os second-box-container">
                                            {!!props.prism.osHPrismBaseDir
                                                ? props.prism.osHPrismBaseDir
                                                : placeHolder}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="pd-parameters-container mobile-headers">
                                <div className="pd-header">
                                    <div className="header-span">
                                        {getLabelFallback(
                                            'MYPROFILE_MY_PRESCRIPTIONS_PD_MODAL'
                                        )}
                                    </div>
                                </div>
                                {!!props.prescriptionValue.pdRight &&
                                    !!props.prescriptionValue.pdLeft ? (
                                    <>
                                        <div className="pd-unique-value">
                                            {!!props.prescriptionValue.pdRight
                                                ? props.prescriptionValue.pdRight
                                                : placeHolder}
                                        </div>
                                        <div className="pd-unique-value ">
                                            {!!props.prescriptionValue.pdLeft
                                                ? props.prescriptionValue.pdLeft
                                                : placeHolder}
                                        </div>
                                    </>
                                ) : (
                                    <div className="pd-unique-value long">
                                        {!!props.prescriptionValue.pupillaryDistance
                                            ? props.prescriptionValue.pupillaryDistance
                                            : placeHolder}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default PrescriptionPreview;
